(function () {
  if (!window.Vue) {
    console.error('Vue is required for product.js');
    return;
  }

  const appRoot = document.querySelector('#product-app');
  if (!appRoot) return;

  const sectionRoot = appRoot.closest('[data-section]');
  const sectionId = sectionRoot?.dataset.section;
  const dataElement = sectionId
    ? document.getElementById(`product-variant-data-${sectionId}`)
    : null;

  let productVariants = [];
  try {
    productVariants = dataElement ? JSON.parse(dataElement.textContent) : [];
  } catch (error) {
    console.error('Unable to parse product variant data:', error);
  }

  const initialVariantId = Number(appRoot.dataset.initialVariantId || 0);
  let initialVariant =
    productVariants.find((item) => Number(item.id) === initialVariantId) ||
    productVariants[0] || {
      id: 0,
      available: false,
      price: '',
      compare_at_price: '',
      price_per_serving: '',
      selling_plans: [],
    };

  const { createApp, ref, onMounted, computed, nextTick } = Vue;

  const updateUrl = (variantId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  };

  const app = createApp({
    setup() {
      const isMounted = ref(false);
      const selectedOption = ref(
        initialVariant.selling_plans?.length ? 'subscribe' : 'one-time'
      );

      const sellingPlanInput = ref(null);
      const sellingPlansSelect = ref(null);
      const variantInput = ref(null);
      const subscriptionPrice = ref(null);
      const subscriptionSaveText = ref(null);
      const pricePerServing = ref(null);
      const oneTimePricePerServing = ref(null);
      const oneTimePrice = ref(null);
      const oneTimeComparePrice = ref(null);
      const addToCartButton = ref(null);

      const variant = ref({ ...initialVariant });
      const selectedSellingPlanId = ref(
        initialVariant.selling_plans?.[0]?.id || ''
      );

      const readLess = ref(true);
      const readMore = ref(false);

      const hasSellingPlan = computed(() => {
        return Array.isArray(variant.value.selling_plans) && variant.value.selling_plans.length > 0;
      });

      const subscribeClass = computed(() =>
        selectedOption.value === 'subscribe' ? 'subscribeClass' : 'oneTimeClass'
      );

      const oneTimeClass = computed(() =>
        selectedOption.value === 'one-time' ? 'subscribeClass' : 'oneTimeClass'
      );

      const getCurrentPlan = () => {
        if (!hasSellingPlan.value) return null;

        return (
          variant.value.selling_plans.find(
            (plan) => String(plan.id) === String(selectedSellingPlanId.value)
          ) || variant.value.selling_plans[0]
        );
      };

      const updatePricePerServing = (newPrice) => {
        if (pricePerServing.value) {
          pricePerServing.value.textContent = newPrice || '';
        }

        if (oneTimePricePerServing.value) {
          oneTimePricePerServing.value.textContent = newPrice || '';
        }
      };

      const syncSellingPlanInput = () => {
        if (!sellingPlanInput.value) return;

        const plan = getCurrentPlan();
        const shouldSubscribe = selectedOption.value === 'subscribe' && Boolean(plan);

        if (shouldSubscribe) {
          sellingPlanInput.value.disabled = false;
          sellingPlanInput.value.value = String(plan.id);
        } else {
          // This is important: one-time purchases and Mini must submit NO selling_plan.
          sellingPlanInput.value.value = '';
          sellingPlanInput.value.disabled = true;
        }
      };

      const updateSubscriptionDisplay = () => {
        const plan = getCurrentPlan();

        if (subscriptionPrice.value) {
          subscriptionPrice.value.textContent = plan?.price || '';
        }

        if (subscriptionSaveText.value) {
          if (
            plan &&
            Number(plan.compare_at_price_cents) > 0 &&
            Number(plan.compare_at_price_cents) > Number(plan.price_cents)
          ) {
            const discount = Math.round(
              ((Number(plan.compare_at_price_cents) - Number(plan.price_cents)) * 100) /
                Number(plan.compare_at_price_cents)
            );
            subscriptionSaveText.value.textContent = ` an additional ${discount}%`;
          } else {
            subscriptionSaveText.value.textContent = '';
          }
        }
      };

      const renderSellingPlanOptions = () => {
        if (!sellingPlansSelect.value) return;

        sellingPlansSelect.value.innerHTML = '';

        variant.value.selling_plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = plan.name;
          sellingPlansSelect.value.appendChild(option);
        });

        const plan = getCurrentPlan();
        if (plan) {
          selectedSellingPlanId.value = plan.id;
          sellingPlansSelect.value.value = String(plan.id);
        }
      };

      const updateOneTimeDisplay = () => {
        if (oneTimePrice.value) {
          oneTimePrice.value.textContent = variant.value.price || '';
        }

        if (oneTimeComparePrice.value) {
          const comparePrice = variant.value.compare_at_price || '';
          oneTimeComparePrice.value.textContent = comparePrice;
          oneTimeComparePrice.value.classList.toggle('hidden', !comparePrice);
        }

        updatePricePerServing(variant.value.price_per_serving);
      };

      const updateVariantMetadata = (variantId) => {
        appRoot.querySelectorAll('.variant-metadata').forEach((element) => {
          element.classList.add('hidden');
        });

        const selectedElement = appRoot.querySelector(`[id="${variantId}"]`);
        if (selectedElement) {
          selectedElement.classList.remove('hidden');
        }
      };

      const setPurchaseMode = (mode) => {
        if (mode === 'subscribe' && !hasSellingPlan.value) {
          mode = 'one-time';
        }

        selectedOption.value = mode;
        syncSellingPlanInput();
      };

      const applyVariant = async (data, shouldUpdateUrl = true) => {
        if (!data) return;

        if (shouldUpdateUrl) {
          updateUrl(data.id);
        }

        variant.value = { ...data };

        if (variantInput.value) {
          variantInput.value.value = String(data.id);
          variantInput.value.disabled = false;
        }

        updateOneTimeDisplay();
        updateVariantMetadata(data.id);

        if (!data.selling_plans?.length) {
          // Mini: force one-time and completely remove the selling-plan value.
          selectedSellingPlanId.value = '';
          selectedOption.value = 'one-time';
          syncSellingPlanInput();
          return;
        }

        // Max: use the first real Shopify/Seal allocation unless the current selection is still valid.
        const stillValid = data.selling_plans.some(
          (plan) => String(plan.id) === String(selectedSellingPlanId.value)
        );
        selectedSellingPlanId.value = stillValid
          ? selectedSellingPlanId.value
          : data.selling_plans[0].id;

        await nextTick();
        renderSellingPlanOptions();
        updateSubscriptionDisplay();

        // On first Max load subscribe is the default. If the shopper explicitly chose
        // one-time earlier, keep that choice while switching back to Max.
        if (selectedOption.value !== 'one-time') {
          selectedOption.value = 'subscribe';
        }

        syncSellingPlanInput();
      };

      const onVariant = async (variantId) => {
        const data = productVariants.find(
          (item) => Number(item.id) === Number(variantId)
        );

        if (!data) {
          console.error('Variant data not found:', variantId);
          return;
        }

        await applyVariant(data, true);
      };

      const onSubscription = (option) => {
        setPurchaseMode(option?.selectedOption || 'one-time');
      };

      const handleChange = (event) => {
        selectedSellingPlanId.value = event.target.value;
        selectedOption.value = 'subscribe';
        updateSubscriptionDisplay();
        syncSellingPlanInput();
      };

      const handleReadMore = () => {
        readLess.value = !readLess.value;
        readMore.value = !readMore.value;
      };

      onMounted(async () => {
        isMounted.value = true;

        const params = new URLSearchParams(window.location.search);
        const buyMode = params.get('buy');

        initialVariant =
          productVariants.find((item) => Number(item.id) === initialVariantId) ||
          productVariants[0] || initialVariant;

        // Establish the current variant first, then decide purchase mode.
        await applyVariant(initialVariant, false);

        if (!hasSellingPlan.value) {
          setPurchaseMode('one-time');
        } else if (buyMode === 'onetime') {
          setPurchaseMode('one-time');
        } else {
          setPurchaseMode('subscribe');
        }
      });

      return {
        isMounted,
        sellingPlanInput,
        sellingPlansSelect,
        variantInput,
        variant,
        selectedOption,
        hasSellingPlan,
        subscribeClass,
        oneTimeClass,
        subscriptionPrice,
        subscriptionSaveText,
        pricePerServing,
        oneTimePricePerServing,
        oneTimePrice,
        oneTimeComparePrice,
        addToCartButton,
        readLess,
        readMore,
        handleReadMore,
        onVariant,
        onSubscription,
        handleChange,
      };
    },
  });

  app.mount(appRoot);

  // Product tabs: scope all selectors to this product section.
  const tabsRoot = sectionRoot?.querySelector('#product-tabs');
  if (tabsRoot) {
    const tabs = tabsRoot.querySelectorAll('.tab');
    const tabAccordionItems = tabsRoot.querySelectorAll('.tab-accordion-item');
    const tabContents = tabsRoot.querySelectorAll('.tab-content-inner');

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        tabs.forEach((item) => item.classList.remove('active'));
        tabContents.forEach((content) => {
          content.classList.remove('active');
          content.classList.remove('open');
          content.style.display = 'none';
        });

        tab.classList.add('active');

        if (tabContents[index]) {
          tabContents[index].classList.add('active');
          tabContents[index].style.display = 'block';
        }

        tabAccordionItems.forEach((element) => element.classList.remove('active'));
        const accordionId = tab.dataset.target;
        const accordion = accordionId
          ? tabsRoot.querySelector(`#${CSS.escape(accordionId)}`)
          : null;
        accordion?.classList.add('active');
      });
    });

    tabAccordionItems.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetId = tab.dataset.target;
        const target = targetId
          ? tabsRoot.querySelector(`#${CSS.escape(targetId)}`)
          : null;
        target?.click();
      });
    });
  }

  const initGallery = () => {
    if (!sectionRoot) return;

    if (window.Fancybox) {
      Fancybox.bind('[data-fancybox="gallery"]');
    }

    if (!window.Swiper) return;

    const thumbElement = sectionRoot.querySelector('.thumb-swiper');
    const mainElement = sectionRoot.querySelector('.main-swiper');

    if (!thumbElement || !mainElement) return;

    const thumbSwiper =
      thumbElement.swiper ||
      new Swiper(thumbElement, {
        direction: 'vertical',
        slidesPerView: 4,
        spaceBetween: 10,
        watchSlidesProgress: true,
        loop: false,
        navigation: {
          nextEl: sectionRoot.querySelector('.swiper-button-next-thumb'),
          prevEl: sectionRoot.querySelector('.swiper-button-prev-thumb'),
        },
      });

    if (!mainElement.swiper) {
      new Swiper(mainElement, {
        spaceBetween: 10,
        thumbs: {
          swiper: thumbSwiper,
        },
        breakpoints: {
          0: {
            autoHeight: true,
            slidesPerView: 1,
            loop: true,
          },
          768: {
            slidesPerView: 1,
            loop: false,
            autoHeight: false,
          },
        },
        navigation: {
          nextEl: sectionRoot.querySelector('.main-swiper .swiper-button-next-custom'),
          prevEl: sectionRoot.querySelector('.main-swiper .swiper-button-prev-custom'),
        },
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallery, { once: true });
  } else {
    initGallery();
  }
})();